import { Box, Stack, Text, Typography } from '@/ui';
import {
  FileIcon,
  PlayArrowIcon,
  PlayCircleFilledWhiteOutlinedIcon,
  SuitesIcon,
  TestsIcon,
  UploadFile,
} from '@/ui/primitives';
import {
  buildHowFlowQaWorksDiagramNodeGroupSx,
  howFlowQaWorksDiagramConnectorSx,
  howFlowQaWorksDiagramFrameSx,
  howFlowQaWorksDiagramNodeSx,
  howFlowQaWorksDiagramPrefixSx,
  howFlowQaWorksDiagramRowSx,
} from './HowFlowQaWorksDiagram.styles';
import type { ReactElement } from 'react';

const diagramRows = [
  {
    prefix: '',
    nodes: [{ icon: <UploadFile fontSize="small" />, label: 'Uploads' }],
  },
  {
    prefix: ' └ ',
    nodes: [{ icon: <FileIcon fontSize="small" />, label: 'Results Files' }],
  },
  {
    prefix: '    └ ',
    nodes: [
      {
        icon: <PlayCircleFilledWhiteOutlinedIcon fontSize="small" />,
        label: 'Execution Runs',
      },
    ],
  },
  {
    prefix: '       └ ',
    nodes: [
      { icon: <SuitesIcon fontSize="small" />, label: 'Suites' },
      { icon: <PlayArrowIcon fontSize="small" />, label: 'Suite Runs' },
    ],
  },
  {
    prefix: '          └ ',
    nodes: [
      { icon: <TestsIcon fontSize="small" />, label: 'Tests' },
      { icon: <PlayArrowIcon fontSize="small" />, label: 'Test Runs' },
    ],
  },
] as const;

export const HowFlowQaWorksDiagram = (): ReactElement => (
  <Stack gap="md">
    <Box sx={howFlowQaWorksDiagramFrameSx}>
      <Stack gap="xs" align="stretch">
        {diagramRows.map((row) => (
          <Box
            key={`${row.prefix}${row.nodes.map((node) => node.label).join('-')}`}
            sx={howFlowQaWorksDiagramRowSx}
          >
            <Typography variant="mono" component="span" sx={howFlowQaWorksDiagramPrefixSx}>
              {row.prefix}
            </Typography>
            <Box sx={buildHowFlowQaWorksDiagramNodeGroupSx(row.nodes.length > 1)}>
              {row.nodes.map((node, index) => (
                <Box key={node.label} sx={howFlowQaWorksDiagramNodeSx}>
                  {node.icon}
                  <Text role="body">{node.label}</Text>
                  {index < row.nodes.length - 1 ? (
                    <Typography
                      variant="mono"
                      component="span"
                      sx={howFlowQaWorksDiagramConnectorSx}
                    >
                      -
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>

    <Stack gap="sm">
      <Text role="body" color="textSecondary">
        Suites and Tests are the stable definitions FlowQA tracks over time.
      </Text>
      <Text role="body" color="textSecondary">
        Suite Runs and Test Runs are the per-execution instances created from each uploaded results
        file.
      </Text>
      <Text role="body" color="textSecondary">
        That structure lets FlowQA compare quality signals across runs instead of treating every
        upload as isolated data.
      </Text>
    </Stack>
  </Stack>
);
